import { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { cognitoIdState } from "features/auth/state/authAtoms";
import {
  allCocoonsLoadingState,
  allCocoonsState,
  allModelsLoadingState,
  allModelsState,
  allMorphedLoadingState,
  allMorphedState,
  allTiresLoadingState,
  allTiresState,
  allWheelhousesLoadingState,
  allWheelhousesState,
  displayModelState,
  fileRenderKeyState,
  S3File
} from "features/file-browser/state/fileAtoms";
import { fetchS3Objects } from "features/file-browser/api/fetchS3Objects";

const getModelName = (displayModel: S3File | null) => {
  if (!displayModel) return null;
  return displayModel.name.replace(/\.[^/.]+$/, "");
};

export const useFetchS3Objects = () => {
  const cognitoId = useRecoilValue(cognitoIdState);
  const displayModel = useRecoilValue(displayModelState);
  const renderKey = useRecoilValue(fileRenderKeyState);

  const setAllModels = useSetRecoilState(allModelsState);
  const setAllCocoons = useSetRecoilState(allCocoonsState);
  const setAllTires = useSetRecoilState(allTiresState);
  const setAllWheelhouses = useSetRecoilState(allWheelhousesState);
  const setAllMorphed = useSetRecoilState(allMorphedState);

  const setModelLoading = useSetRecoilState(allModelsLoadingState);
  const setCocoonLoading = useSetRecoilState(allCocoonsLoadingState);
  const setTireLoading = useSetRecoilState(allTiresLoadingState);
  const setWheelhouseLoading = useSetRecoilState(allWheelhousesLoadingState);
  const setMorphedLoading = useSetRecoilState(allMorphedLoadingState);

  const [modelName, setModelName] = useState<string>('');

  useEffect(() => {
    if (displayModel) {
      const res = getModelName(displayModel);
      if (res) setModelName(res);
    }
  }, [displayModel]);

  useEffect(() => {
    if (cognitoId) {
      const fetchFiles = async () => {
        setModelLoading(true);
        setAllModels(await fetchS3Objects(cognitoId, 'model/'));
        setModelLoading(false);
      };
      fetchFiles();
    } else {
      setModelLoading(true);
    }
  }, [renderKey, cognitoId, setAllModels, setModelLoading]);

  useEffect(() => {
    if (cognitoId) {
      const fetchFiles = async () => {
        setCocoonLoading(true);
        setAllCocoons(await fetchS3Objects(cognitoId, `cocoon/${modelName}/`));
        setCocoonLoading(false);
      };
      fetchFiles();
    } else {
      setCocoonLoading(true);
    }
  }, [renderKey, cognitoId, modelName, setAllCocoons, setCocoonLoading]);

  useEffect(() => {
    if (cognitoId && modelName) {
      const fetchFiles = async () => {
        setTireLoading(true);
        setAllTires(await fetchS3Objects(cognitoId, `tire/${modelName}/`));
        setTireLoading(false);
      };
      fetchFiles();
    } else {
      setTireLoading(true);
    }
  }, [renderKey, cognitoId, modelName, setAllTires, setTireLoading]);

  useEffect(() => {
    if (cognitoId && modelName) {
      const fetchFiles = async () => {
        setWheelhouseLoading(true);
        setAllWheelhouses(await fetchS3Objects(cognitoId, `wheelhouse/${modelName}/`));
        setWheelhouseLoading(false);
      };
      fetchFiles();
    } else {
      setWheelhouseLoading(true);
    }
  }, [renderKey, cognitoId, modelName, setAllWheelhouses, setWheelhouseLoading]);

  useEffect(() => {
    if (cognitoId) {
      const fetchFiles = async () => {
        setMorphedLoading(true);
        setAllMorphed(await fetchS3Objects(cognitoId, `morphed/`));
        setMorphedLoading(false);
      };
      fetchFiles();
    } else {
      setMorphedLoading(true);
    }
  }, [renderKey, cognitoId, setAllMorphed, setMorphedLoading]);
};
